import type { Path, PathValue, UseFormReturn } from "react-hook-form";
import type { LockCalculatorFormValues } from "../../model/form";
import { catalogSeverity, type NoticeCode } from "../../model/noticeCatalog";
import type { CascadeRecord, NoticeSeverity } from "../../model/notices";
import type { ReportCascade } from "./NoticesContext";

type FormPath = Path<LockCalculatorFormValues>;

export type CascadeSpec<P extends FormPath> = {
  /** Stable key of the message (i18n). */
  code: NoticeCode;
  /** Fields whose value triggered the change. */
  causes: FormPath[];
  effect: P;
  next: PathValue<LockCalculatorFormValues, P>;
  params?: Record<string, unknown>;
  severity?: NoticeSeverity;
  /** Passed to setValue. */
  options?: { shouldDirty?: boolean; shouldValidate?: boolean };
  /** Change the field without a notice (the effect of a cascade already reported). */
  silent?: boolean;
};

/** The record of a change already made: the causes' current values and the value the effect now holds. */
export const buildCascadeRecord = <P extends FormPath>(
  form: UseFormReturn<LockCalculatorFormValues>,
  spec: Omit<CascadeSpec<P>, "options" | "silent">,
): CascadeRecord => ({
  id: `${spec.code}:${spec.effect}`,
  code: spec.code,
  severity: spec.severity ?? catalogSeverity(spec.code),
  causes: spec.causes.map((path) => ({ path, value: form.getValues(path) })),
  effect: { path: spec.effect, value: spec.next },
  params: spec.params ?? {},
});

/**
 * Sets a field because of other fields and records it as "x changed because of y". Nothing happens
 * when the field already holds the value, so a rule re-running after a form reset reports nothing.
 */
export const applyCascade = <P extends FormPath>(
  form: UseFormReturn<LockCalculatorFormValues>,
  report: ReportCascade,
  spec: CascadeSpec<P>,
): void => {
  if ((form.getValues(spec.effect) ?? null) === (spec.next ?? null)) return;
  form.setValue(spec.effect, spec.next, spec.options);
  if (spec.silent) return;
  report(buildCascadeRecord(form, spec));
};
